import React, { useEffect, useState } from "react";
import { Button, Form, Alert, Badge } from "react-bootstrap";
import { listPackages, createPackage, updatePackage, deletePackage } from "../services/partnerAuthService";

const getErr = (err, fallback) => err.response?.data?.error || fallback;

const emptyForm = {
  title: "", description: "", destinationCities: "", price: "", durationDays: "1",
  inclusions: "", exclusions: "", cancellationPolicy: "", maxTravellers: "1",
};
const emptyRefundPolicy = { freeCancellationHours: 24, partialRefundPercent: 50, nonRefundableHours: 6 };

const APPROVAL_TONE = { Pending: "secondary", Approved: "success", Rejected: "danger", ChangesRequested: "warning" };
const APPROVAL_LABEL = { Pending: "Pending review", Approved: "Approved", Rejected: "Rejected", ChangesRequested: "Changes requested" };

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [itinerary, setItinerary] = useState([{ day: 1, plan: "" }]);
  const [addOns, setAddOns] = useState([{ title: "", price: "" }]);
  const [refundPolicy, setRefundPolicy] = useState(emptyRefundPolicy);
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingWasApproved, setEditingWasApproved] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listPackages()
      .then(({ data }) => setPackages(data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const resetForm = () => {
    setForm(emptyForm);
    setItinerary([{ day: 1, plan: "" }]);
    setAddOns([{ title: "", price: "" }]);
    setRefundPolicy(emptyRefundPolicy);
    setImageFiles([]);
    setExistingImages([]);
    setEditingId(null);
    setEditingWasApproved(false);
  };

  const startAdd = () => {
    resetForm();
    setShowForm(true);
    setError("");
  };

  const cancelForm = () => {
    resetForm();
    setShowForm(false);
  };

  const startEdit = (pkg) => {
    setForm({
      title: pkg.title || "",
      description: pkg.description || "",
      destinationCities: (pkg.destinationCities || []).join(", "),
      price: pkg.price ?? "",
      durationDays: pkg.durationDays ?? "1",
      inclusions: (pkg.inclusions || []).join(", "),
      exclusions: (pkg.exclusions || []).join(", "),
      cancellationPolicy: pkg.cancellationPolicy || "",
      maxTravellers: pkg.maxTravellers ?? "1",
    });
    setItinerary(pkg.itinerary?.length ? pkg.itinerary.map((d) => ({ day: d.day, plan: d.plan })) : [{ day: 1, plan: "" }]);
    setAddOns(pkg.addOns?.length ? pkg.addOns.map((a) => ({ title: a.title, price: a.price })) : [{ title: "", price: "" }]);
    setRefundPolicy({
      freeCancellationHours: pkg.refundPolicy?.freeCancellationHours ?? 24,
      partialRefundPercent: pkg.refundPolicy?.partialRefundPercent ?? 50,
      nonRefundableHours: pkg.refundPolicy?.nonRefundableHours ?? 6,
    });
    setExistingImages(pkg.images || []);
    setImageFiles([]);
    setEditingId(pkg._id);
    setEditingWasApproved(pkg.approvalStatus === "Approved");
    setShowForm(true);
    setError("");
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Itinerary builder ──────────────────────────────────────────────────
  const addDay = () => setItinerary((it) => [...it, { day: it.length + 1, plan: "" }]);
  const removeDay = (index) =>
    setItinerary((it) => it.filter((_, i) => i !== index).map((d, i) => ({ ...d, day: i + 1 })));
  const updateDayPlan = (index, plan) =>
    setItinerary((it) => it.map((d, i) => (i === index ? { ...d, plan } : d)));

  // ── Add-ons builder ─────────────────────────────────────────────────────
  const addAddOn = () => setAddOns((a) => [...a, { title: "", price: "" }]);
  const removeAddOn = (index) => setAddOns((a) => a.filter((_, i) => i !== index));
  const updateAddOn = (index, field, value) =>
    setAddOns((a) => a.map((row, i) => (i === index ? { ...row, [field]: value } : row)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    if (!form.title || !form.price) return setError("Title and price are required");

    const cleanItinerary = itinerary.filter((d) => d.plan && d.plan.trim());
    const cleanAddOns = addOns
      .filter((a) => a.title && a.title.trim() && a.price !== "")
      .map((a) => ({ title: a.title, price: Number(a.price) || 0 }));
    const cleanRefundPolicy = {
      freeCancellationHours: Number(refundPolicy.freeCancellationHours) || 0,
      partialRefundPercent: Math.min(100, Math.max(0, Number(refundPolicy.partialRefundPercent) || 0)),
      nonRefundableHours: Number(refundPolicy.nonRefundableHours) || 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        // PATCH /partner/packages/:id has no image-upload support server-side, so this is a plain
        // JSON payload — matches updateService's pattern.
        const payload = {
          ...form,
          itinerary: JSON.stringify(cleanItinerary),
          addOns: JSON.stringify(cleanAddOns),
          refundPolicy: JSON.stringify(cleanRefundPolicy),
        };
        await updatePackage(editingId, payload);
        setNotice(editingWasApproved ? "Package updated. Your changes will be reviewed again before going live." : "Package updated.");
      } else {
        // POST /partner/packages accepts image files, so it must be multipart/form-data.
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append("itinerary", JSON.stringify(cleanItinerary));
        fd.append("addOns", JSON.stringify(cleanAddOns));
        fd.append("refundPolicy", JSON.stringify(cleanRefundPolicy));
        imageFiles.forEach((file) => fd.append("images", file));
        await createPackage(fd);
        setNotice("Package submitted for review.");
      }
      resetForm();
      setShowForm(false);
      load();
    } catch (err) {
      setError(getErr(err, editingId ? "Failed to update package" : "Failed to create package"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (pkg) => {
    try {
      await updatePackage(pkg._id, { isActive: !pkg.isActive });
      load();
    } catch (err) {
      setError(getErr(err, "Failed to update package"));
    }
  };

  const remove = async (pkg) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await deletePackage(pkg._id);
      load();
    } catch (err) {
      setError(getErr(err, "Failed to delete package"));
    }
  };

  if (loading) return <p>Loading packages…</p>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">My Packages</h4>
        <Button size="sm" onClick={() => (showForm ? cancelForm() : startAdd())}>{showForm ? "Cancel" : "+ Add Package"}</Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="info" onClose={() => setNotice("")} dismissible>{notice}</Alert>}

      {showForm && (
        <div className="partner-card mb-4">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-2">
              <Form.Label>Title</Form.Label>
              <Form.Control placeholder="e.g. 4N/5D Goa Beach Holiday" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Destination Cities (comma separated)</Form.Label>
              <Form.Control value={form.destinationCities} onChange={(e) => setForm({ ...form, destinationCities: e.target.value })} />
            </Form.Group>
            <div className="d-flex gap-3 flex-wrap">
              <Form.Group className="mb-2" style={{ minWidth: 160 }}>
                <Form.Label>Price (₹)</Form.Label>
                <Form.Control type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2" style={{ minWidth: 160 }}>
                <Form.Label>Duration (days)</Form.Label>
                <Form.Control type="number" min={1} value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2" style={{ minWidth: 160 }}>
                <Form.Label>Max Travellers</Form.Label>
                <Form.Control type="number" min={1} value={form.maxTravellers} onChange={(e) => setForm({ ...form, maxTravellers: e.target.value })} />
              </Form.Group>
            </div>
            <Form.Group className="mb-2">
              <Form.Label>Inclusions (comma separated)</Form.Label>
              <Form.Control value={form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} placeholder="e.g. Hotel stay, Breakfast, Airport transfer" />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Exclusions (comma separated)</Form.Label>
              <Form.Control value={form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} placeholder="e.g. Flights, Personal expenses" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cancellation Policy (free text, shown to customers)</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.cancellationPolicy} onChange={(e) => setForm({ ...form, cancellationPolicy: e.target.value })} />
            </Form.Group>

            {!editingId && (
              <Form.Group className="mb-3">
                <Form.Label>Photos</Form.Label>
                <Form.Control type="file" accept="image/*" multiple onChange={(e) => setImageFiles(Array.from(e.target.files || []))} />
                <Form.Text muted>Optional. Up to 10 images.</Form.Text>
              </Form.Group>
            )}
            {editingId && existingImages.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Current Photos</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {existingImages.map((url, i) => (
                    <img key={i} src={url} alt={`package ${i + 1}`} style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }} />
                  ))}
                </div>
                <Form.Text muted>Photos can only be added when the package is first created; contact support to change images on an existing package.</Form.Text>
              </Form.Group>
            )}

            <div className="partner-card mb-3" style={{ background: "#f9fafb" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Day-wise Itinerary</h6>
                <Button size="sm" variant="outline-secondary" onClick={addDay} type="button">+ Add Day</Button>
              </div>
              {itinerary.map((d, i) => (
                <div key={i} className="d-flex gap-2 align-items-start mb-2">
                  <div className="fw-bold pt-2" style={{ minWidth: 56 }}>Day {d.day}</div>
                  <Form.Control
                    as="textarea" rows={2}
                    placeholder="What happens on this day…"
                    value={d.plan}
                    onChange={(e) => updateDayPlan(i, e.target.value)}
                  />
                  <Button size="sm" variant="outline-danger" type="button" onClick={() => removeDay(i)} disabled={itinerary.length === 1}>✕</Button>
                </div>
              ))}
            </div>

            <div className="partner-card mb-3" style={{ background: "#f9fafb" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Add-ons</h6>
                <Button size="sm" variant="outline-secondary" onClick={addAddOn} type="button">+ Add Add-on</Button>
              </div>
              {addOns.map((a, i) => (
                <div key={i} className="d-flex gap-2 align-items-center mb-2">
                  <Form.Control placeholder="e.g. Scuba Diving Session" value={a.title} onChange={(e) => updateAddOn(i, "title", e.target.value)} />
                  <Form.Control type="number" min={0} placeholder="Price ₹" style={{ maxWidth: 140 }} value={a.price} onChange={(e) => updateAddOn(i, "price", e.target.value)} />
                  <Button size="sm" variant="outline-danger" type="button" onClick={() => removeAddOn(i)} disabled={addOns.length === 1}>✕</Button>
                </div>
              ))}
            </div>

            <div className="partner-card mb-3" style={{ background: "#f9fafb" }}>
              <h6 className="mb-3">Cancellation Policy (structured refund tiers)</h6>
              <Form.Group className="mb-2">
                <Form.Label>Free cancellation up to (hours before trip)</Form.Label>
                <Form.Control
                  type="number" min={0}
                  value={refundPolicy.freeCancellationHours}
                  onChange={(e) => setRefundPolicy({ ...refundPolicy, freeCancellationHours: e.target.value })}
                />
                <Form.Text muted>Customer gets a full refund if they cancel this many hours before the trip.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Partial refund (%)</Form.Label>
                <Form.Control
                  type="number" min={0} max={100}
                  value={refundPolicy.partialRefundPercent}
                  onChange={(e) => setRefundPolicy({ ...refundPolicy, partialRefundPercent: e.target.value })}
                />
                <Form.Text muted>Percentage refunded for cancellations after the free window but before the non-refundable window.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-1">
                <Form.Label>No refund within (hours of trip)</Form.Label>
                <Form.Control
                  type="number" min={0}
                  value={refundPolicy.nonRefundableHours}
                  onChange={(e) => setRefundPolicy({ ...refundPolicy, nonRefundableHours: e.target.value })}
                />
                <Form.Text muted>Cancellations inside this window are not refunded.</Form.Text>
              </Form.Group>
            </div>

            <Button type="submit" disabled={saving}>{saving ? "Saving…" : editingId ? "Save Changes" : "Save Package"}</Button>
          </Form>
        </div>
      )}

      {packages.length === 0 ? (
        <div className="partner-card partner-empty">Add your first package to start selling bundled trips directly to customers.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {packages.map((p) => (
            <div key={p._id} className="partner-card">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h6 className="mb-1">
                    {p.title}{" "}
                    <Badge bg={p.isActive ? "success" : "secondary"}>{p.isActive ? "Active" : "Paused"}</Badge>{" "}
                    <Badge bg={APPROVAL_TONE[p.approvalStatus] || "secondary"}>{APPROVAL_LABEL[p.approvalStatus] || p.approvalStatus}</Badge>
                  </h6>
                  <small className="text-muted">₹{p.price} · {p.durationDays} day(s) · {(p.destinationCities || []).join(", ") || "Any destination"}</small>
                </div>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => startEdit(p)}>Edit</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => toggleActive(p)}>{p.isActive ? "Pause" : "Resume"}</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => remove(p)}>Delete</Button>
                </div>
              </div>

              {p.approvalStatus === "ChangesRequested" && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <strong>Admin requested changes:</strong>{" "}
                  {p.approvalRemark || "Please review and update this listing."}
                  <div className="mt-2">
                    <Button size="sm" variant="warning" onClick={() => startEdit(p)}>Edit &amp; Resubmit</Button>
                  </div>
                </Alert>
              )}
              {p.approvalStatus === "Rejected" && (
                <Alert variant="danger" className="mt-3 mb-0">
                  <strong>Rejected:</strong> {p.approvalRemark || "This listing did not meet approval requirements."}
                  <div className="mt-2">
                    <Button size="sm" variant="outline-danger" onClick={() => startEdit(p)}>Edit &amp; Resubmit</Button>
                  </div>
                </Alert>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
